import React, { FC, useRef } from 'react';

import {
  Button,
  Flex,
  Heading,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';

import { MdAddLocationAlt } from 'react-icons/md';

import {
  AddPickupModal,
  AddPickupModalRef,
} from 'components/modules/AddPickupModal';
import { UserPill } from 'components/core/UserPill';

import { PickupsViewProps } from './Pickups.props';
import { Status } from 'types/items';
import DateStatCard from 'components/primitives/DateStatCard';
import { useQuery } from 'react-query';
import { getPickupCounts } from 'api/pickup';
import { washHub } from 'store';
import { useMutation } from 'react-query';
import { exportPickupsCsv } from 'api/pickup';
import { AllPickups } from './AllPickups';
import { OpenPickups } from './OpenPickups';
import { ClosePickups } from './ClosePickups';
import { PendingPickups } from './PendingPickups';
import { Filters } from 'components/primitives/Filters';
import { DateRangeInputExt } from 'components/modules/DateRangeInputExt';

const PickupsView: FC<PickupsViewProps> = (props) => {
  const {
    venue,
    status,
    endDate,
    startDate,
    isDeleting,
    defaultIndex,
    setDefaultIndex,
    onDelete,
    onApplyFilters,
    onSearchClear,
    onSearchPress,
    onStatusClick,
    onClearFilters,
  } = props;
  const _focus = { border: 'none', backgroundColor: 'primary.500' };
  const _selected = { backgroundColor: 'primary.900', color: 'support.500' };
  const addPickupModalRef = useRef<AddPickupModalRef>(null);
  const selectedWashHub = washHub.selectors.useSelectedWashHub();
  const washHubId = selectedWashHub?._id || '';

  const { data: pickupsCount } = useQuery(
    [
      `fetch-pickups-status-count-${washHubId}`,
      startDate,
      endDate,
      venue?.toString(),
    ],
    () => getPickupCounts(washHubId, startDate, endDate, venue),
  );

  const openPickups = pickupsCount?.[Status.OPEN] || 0;
  const closePickups =
    (pickupsCount?.[Status.CLOSE] || 0) +
    (pickupsCount?.[Status.EMPTY_BIN] || 0);
  const pendingCount = pickupsCount?.[Status.PENDING_COUNT] || 0;

  const allPickups = openPickups + closePickups + pendingCount;

  const csvRef = useRef<any>(null);

  const { mutate, data: csvData = '', isLoading: isExporting } = useMutation(
    async () => {
      let response: string = '';

      let status = undefined;
      switch (defaultIndex) {
        case 1:
          status = Status.OPEN;
          break;
        case 2:
          status = Status.CLOSE;
          break;
        case 3:
          status = Status.PENDING_COUNT;
          break;
        default:
          status = undefined;
          break;
      }

      response = await exportPickupsCsv({
        washHub: washHubId,
        endDate,
        startDate,
        venue,
        status,
      });

      return response;
    },
    {
      onSuccess: () => {
        setTimeout(() => {
          csvRef.current?.click();
        }, 500);
      },
    },
  );

  const renderExpBtn = () => {
    return (
      <>
        <Button
          isLoading={isExporting}
          ml={2}
          minW="200px"
          onClick={() => {
            mutate();
          }}>
          Export Pickups to CSV
        </Button>
        {Boolean(csvData) && (
          <a
            href={`data:text/csv;charset=utf-8,${csvData}`}
            download={`washhub-${selectedWashHub?.name}-pickups.csv`}
            style={{ display: 'none' }}
            ref={csvRef}>
            Export CSV
          </a>
        )}
      </>
    );
  };

  const _props = {
    endDate,
    startDate,
    isDeleting,
    onDelete,
    venue,
  };

  return (
    <Flex direction="column" flex={1} color="support.500" overflow="auto">
      <Flex justify="space-between">
        <Heading mb={6} fontSize="24px">
          Pickups
        </Heading>
        <Flex align="flex-end">
          <Stack isInline>
            {renderExpBtn()}
            <Button
              isDisabled={washHubId === ''}
              mr={2}
              minW="200px"
              leftIcon={<MdAddLocationAlt size="20px" />}
              onClick={() => {
                addPickupModalRef?.current?.onOpen?.();
              }}>
              Add Pickup
            </Button>
            <UserPill />
          </Stack>
        </Flex>
      </Flex>
      <DateRangeInputExt
        startDate={startDate}
        endDate={endDate}
        onSearchClear={onSearchClear}
        onSearchPress={onSearchPress}
      />
      <DateStatCard status={status as any} onStatusClick={onStatusClick} />
      <Filters
        isFullButton
        isSitesOnly
        venue={venue || []}
        onApplyFilters={onApplyFilters}
        onClearFilters={onClearFilters}
      />
      <Tabs
        variant="enclosed"
        size="md"
        index={defaultIndex || 0}
        defaultIndex={defaultIndex || 0}
        onChange={(item) => {
          setDefaultIndex(item);
        }}>
        <TabList _selected={_selected} _focus={_focus} w="100%">
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            All {`(${allPickups})`}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Scheduled {`(${openPickups})`}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Done {`(${closePickups})`}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Pending Count {`(${pendingCount})`}
          </Tab>
        </TabList>
        <TabPanels p={0}>
          <TabPanel p={0}>
            <AllPickups {..._props} />
          </TabPanel>
          <TabPanel p={0}>
            <OpenPickups {..._props} />
          </TabPanel>
          <TabPanel p={0}>
            <ClosePickups {..._props} />
          </TabPanel>
          <TabPanel p={0}>
            <PendingPickups {..._props} />
          </TabPanel>
        </TabPanels>
      </Tabs>
      <AddPickupModal ref={addPickupModalRef} />
    </Flex>
  );
};

export default PickupsView;
